/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import { App, Form, Input, InputNumber, Tooltip } from 'antd';
import { NumericFormat } from 'react-number-format';
import { withValueCap } from 'pages/globals/global';
import { Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow } from '@mui/material';
import InputLabel from 'components/InputLabel/index';
import { MinusCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';

import { constantFormValues } from '../../root';
import { processArrays } from '../LeaseUtils/helper';

const AdvanceAmount = ({ field, screen = 'CREATE', dataFullyLoaded = false, index }) => {
	const { message: messageApi } = App.useApp();
	const form = Form.useFormInstance();
	// State to track the total amount
	const [totalAmount, setTotalAmount] = useState(0);
	// State to track which values have been modified (for highlighting)
	const [modifiedFields, setModifiedFields] = useState({});
	// State to track row totals
	const [rowTotals, setRowTotals] = useState([]);

	// Use Form.useWatch to monitor the advanceAmount value directly from the form
	const formAdvanceAmount = Form.useWatch(constantFormValues?.advanceAmount?.key, form);

	useEffect(() => {
		if (formAdvanceAmount !== undefined && formAdvanceAmount !== null) {
			// If the form value exists but differs from our state, update our state
			if (formAdvanceAmount !== totalAmount) {
				setTotalAmount(formAdvanceAmount);
			}
		}
	}, [formAdvanceAmount]);

	// Add this useEffect to prevent the form from overriding our calculated total
	useEffect(() => {
		// When totalAmount changes, make sure the form value is updated
		if (totalAmount > 0) {
			const currentFormValue = form.getFieldValue(constantFormValues?.advanceAmount?.key);

			// If the form value differs from our calculated total, update it
			if (currentFormValue !== totalAmount) {
				form.setFieldValue(constantFormValues?.advanceAmount?.key, totalAmount);
			}
		}
	}, [totalAmount]);

	function transformRangeDataToAmountArray(rangeData) {
		// Initialize an empty result array
		const result = [];

		// Process each range object
		rangeData.forEach((range) => {
			const fromValue = parseInt(range.from);
			const toValue = parseInt(range.to);
			const amount = parseFloat(range.amount);

			// Skip invalid ranges
			if (isNaN(fromValue) || isNaN(toValue) || fromValue > toValue) {
				console.warn('Invalid range found:', range);
				return;
			}

			// Skip if amount is not a valid number
			if (isNaN(amount)) {
				console.warn('Invalid amount found:', range);
				return;
			}

			// Calculate how many times to repeat the amount ((to - from) + 1)
			const repetitions = toValue - fromValue + 1;

			// Add the amount to the result array the specified number of times
			for (let i = 0; i < repetitions; i++) {
				result.push(amount);
			}
		});

		return result;
	}

	// Calculate row totals based on current form values
	const calculateRowTotals = () => {
		try {
			// Get the current values from the form
			const values = form.getFieldValue(['leaseSimulator', index, 'advanceAmountList']);
			// console.log('totalRow', values);

			if (values && Array.isArray(values)) {
				const newRowTotals = values.map((item) => {
					if (!item) return 0;

					const fromValue = parseInt(item.from);
					const toValue = parseInt(item.to);
					const amount = parseFloat(item.amount);

					// console.log('validation', fromValue, toValue, amount, isNaN(fromValue), isNaN(toValue), isNaN(amount), toValue < fromValue);
					// Skip invalid values
					if (isNaN(fromValue) || isNaN(toValue) || isNaN(amount) || toValue < fromValue) {
						return 0;
					}

					// Calculate total: (to - from + 1) * amount
					return (toValue - fromValue + 1) * amount;
				});

				// console.log('totalRow', newRowTotals);
				setRowTotals(newRowTotals);
			}
		} catch (error) {
			console.error('Error calculating row totals:', error);
		}
	};

	const calculateTotal = () => {
		try {
			// Calculate row totals directly
			const values = form.getFieldValue(['leaseSimulator', index, 'advanceAmountList']);

			if (values && Array.isArray(values)) {
				// Calculate row totals directly without waiting for state update
				const currentRowTotals = values.map((item) => {
					if (!item) return 0;

					const fromValue = parseInt(item.from);
					const toValue = parseInt(item.to);

					// If amount exists, convert it from string to number
					let amount = 0;
					if (item.amount !== undefined && item.amount !== null && item.amount !== '') {
						amount = parseFloat(item.amount);
					}

					// Skip invalid values
					if (isNaN(fromValue) || isNaN(toValue) || isNaN(amount) || toValue < fromValue) {
						return 0;
					}

					// Calculate total: (to - from + 1) * amount
					return (toValue - fromValue + 1) * amount;
				});

				// console.log('totalRow', currentRowTotals);
				// Update row totals state
				setRowTotals(currentRowTotals);

				// Now calculate the grand total using the current row totals
				const sum = currentRowTotals.reduce((total, rowTotal) => total + (rowTotal || 0), 0);

				// Update total amount state
				setTotalAmount(sum);
			}
		} catch (error) {
			console.error('Error calculating total:', error);
		}
	};

	// Handler for when an amount changes
	const handleAmountChange = (lsIndex, aaIndex) => {
		// Mark this field as modified for highlighting
		setModifiedFields((prev) => ({
			...prev,
			[`LeaseSimulator${lsIndex}AdvanceAmount${aaIndex}Amount`]: true,
		}));

		// Recalculate the total
		setTimeout(() => {
			calculateTotal();
			handleApply();
		}, 10);
	};

	// Handler for "To" value changes
	const handleToChange = (lsIndex, aaIndex) => {
		// Only mark this field as modified for highlighting
		setModifiedFields((prev) => ({
			...prev,
			[`LeaseSimulator${lsIndex}AdvanceAmount${aaIndex}To`]: true,
		}));
		// Calculate row totals when "To" value changes
		setTimeout(() => {
			calculateRowTotals();
			handleApply();
		}, 10);
	};

	// Validate To value ONLY on blur to allow typing multi-digit numbers
	const validateToValueOnBlur = (lsIndex, aaIndex) => {
		const fromValue = parseInt(form.getFieldValue(['leaseSimulator', lsIndex, 'advanceAmountList', aaIndex, 'form']));
		const toValue = parseInt(form.getFieldValue(['leaseSimulator', lsIndex, 'advanceAmountList', aaIndex, 'to']));

		// Validate that To is greater than From
		if (!isNaN(toValue) && !isNaN(fromValue) && toValue < fromValue) {
			messageApi.error("'To' value must be greater than 'From' value");
			form.setFields([
				{
					name: ['leaseSimulator', lsIndex, 'advanceAmountList', aaIndex, 'to'],
					value: '',
					errors: ["'To' value must be greater than 'From' value"],
				},
			]);
		}
		// Recalculate row totals after validation
		calculateRowTotals();
		handleApply();
	};

	// Validate if a row is complete and valid
	const isRowValid = (fromValue, toValue, amount) => {
		if (isNaN(toValue) || toValue <= 0) return false;
		if (isNaN(fromValue) || fromValue <= 0) return false;
		if (toValue < fromValue) return false;
		if (!amount) return false;

		return true;
	};

	// Handle adding a new row - modified to add after a specific row
	const handleAddRow = (lsIndex, aaIndex) => {
		const fromValue = parseInt(form.getFieldValue(['leaseSimulator', lsIndex, 'advanceAmountList', aaIndex, 'from']));
		const toValue = parseInt(form.getFieldValue(['leaseSimulator', lsIndex, 'advanceAmountList', aaIndex, 'to']));
		const amount = parseInt(form.getFieldValue(['leaseSimulator', lsIndex, 'advanceAmountList', aaIndex, 'amount']));

		// Check if the current row is complete
		if (!isRowValid(fromValue, toValue, amount)) {
			messageApi.error('Please complete all required fields in the current row before adding a new one');
			return;
		}

		// // New row's "From" value is current row's "To" value + 1
		// const newFrom = toValue + 1;

		// // Create the new row
		// const newRowId = Math.max(...advanceAmountRows.map((row) => row.id)) + 1;
		// const newRows = [...advanceAmountRows];
		// newRows.splice(aaIndex + 1, 0, { id: newRowId, from: newFrom, to: '', amount: '', totalAmount: '' });

		// // Fix the From values for all subsequent rows
		// for (let i = aaIndex + 2; i < newRows.length; i++) {
		// 	const prevTo = parseInt(form.getFieldValue(`LeaseSimulator${lsIndex}AdvanceAmount${i - 1}To`));
		// 	if (!isNaN(prevTo)) {
		// 		newRows[i].from = prevTo + 1;
		// 		form.setFieldValue(`LeaseSimulator${lsIndex}AdvanceAmount${i}From`, prevTo + 1);
		// 	}
		// }

		// setAdvanceAmountRows(newRows);

		// Set the form value for the new row
		// form.setFieldValue(`LeaseSimulator${lsIndex}AdvanceAmount${aaIndex + 1}From`, newFrom);
		const aa = form.getFieldValue(['leaseSimulator', lsIndex, 'advanceAmountList']) || [];
		form.setFieldValue(['leaseSimulator', lsIndex, 'advanceAmountList'], [...aa, { from: toValue + 1, to: null, amount: null }]);

		// Automatically apply the changes
		setTimeout(handleApply, 10);
	};

	const handleDeleteRow = () => {
		calculateTotal();
		handleApply();
	};

	useEffect(() => {
		if (screen === 'VIEW' && dataFullyLoaded) {
			// Add a larger delay to ensure form data is populated in view mode
			const timeoutId = setTimeout(() => {
				calculateTotal(); // Use this instead of separate calls
			}, 700); // Increased from 500ms

			return () => clearTimeout(timeoutId);
		}
	}, [screen, dataFullyLoaded]);

	const handleApply = () => {
		// Collect data from individual form fields
		// const rangeData = advanceAmountRows.map((row, aaIndex) => {
		// 	return {
		// 		from: form.getFieldValue(`LeaseSimulator${index}AdvanceAmount${aaIndex}From`),
		// 		to: form.getFieldValue(`LeaseSimulator${index}AdvanceAmount${aaIndex}To`),
		// 		amount: form.getFieldValue(`LeaseSimulator${index}AdvanceAmount${aaIndex}Amount`),
		// 		totalAmount: rowTotals[aaIndex] || 0,
		// 	};
		// });

		const rangeData = form.getFieldValue(['leaseSimulator', index, 'advanceAmountList']);
		// console.log('rangeData', rangeData);
		if (!rangeData || rangeData.length === 0) return;

		const transformedData = transformRangeDataToAmountArray(rangeData);
		// console.log('transformedData', transformedData);
		const grossRents = form.getFieldValue(['leaseSimulator', index, 'grossRents']) || [];
		// console.log('grossRents', grossRents);
		const processResult = processArrays(grossRents, transformedData);
		// console.log('processResult', processResult);

		let processedAdvanceAmounts = transformedData;

		const total = transformedData.reduce((sum, amount) => sum + amount, 0);

		if (grossRents && grossRents.length > 0) {
			processedAdvanceAmounts = processResult.processedAdvanceAmounts;
		}

		// Set the advanceAmounts array for API submission
		// form.setFieldValue(`leaseSimulatorRow_${index}_advanceAmounts`, processedAdvanceAmounts);

		// Update local state
		setTotalAmount(total);

		// Update the form
		// form.setFieldsValue({
		// 	[`leaseSimulatorRow_${index}_advanceAmount`]: total,
		// 	[`leaseSimulatorRow_${index}_advanceAmounts`]: processedAdvanceAmounts,
		// });
		form.setFieldValue(['leaseSimulator', index, 'advanceAmount'], total);
		form.setFieldValue(['leaseSimulator', index, 'advanceAmounts'], processedAdvanceAmounts);

		// Force the form to validate and notify watchers
		form.validateFields([
			['leaseSimulator', index, 'advanceAmount'],
			['leaseSimulator', index, 'advanceAmounts'],
		]);
	};

	// Initialize with the first row
	useEffect(() => {
		if (screen !== 'CREATE' && dataFullyLoaded) {
			// Calculate initial total if data exists
			calculateTotal();
			calculateRowTotals();
		}
	}, [dataFullyLoaded]);

	// Ensure advanceAmount in the form stays synchronized with the total
	useEffect(() => {
		// Only update the form when the total actually changes
		if (totalAmount > 0) {
			form.setFieldValue(['leaseSimulator', index, 'advanceAmount'], totalAmount);

			// Also update changes array if it exists
			const currentChanges = form.getFieldValue('changes');
			if (currentChanges && currentChanges.length > 0) {
				// Make a shallow copy to avoid direct state mutation
				const updatedChanges = [...currentChanges];
				updatedChanges[0] = {
					...updatedChanges[0],
					inputChangeAdvance: totalAmount,
				};
				form.setFieldValue('changes', updatedChanges);
			}
		}
	}, [totalAmount]);

	return (
		<>
			<Form.List name={[field.name, 'advanceAmountList']}>
				{(subFields, subFieldsOpt) => (
					<TableContainer
						className="advance-amount-table"
						sx={{
							'& table, & th, & td': {
								border: '1px solid #e0e0e0',
								borderCollapse: 'collapse',
							},
							'& th, & td': {
								p: '5px',
							},
							'& td .ant-form-item': {
								marginBottom: '0px',
							},

							'& th:nth-of-type(1), & td:nth-of-type(1)': { width: '20%' } /* From/From Month */,
							'& th:nth-of-type(2), & td:nth-of-type(2)': { width: '20%' } /* To/To Month */,
							'& th:nth-of-type(3), & td:nth-of-type(3)': { width: '20%' } /* Amount/Monthly Adjustment */,
							'& th:nth-of-type(4), & td:nth-of-type(4)': { width: '20%' } /* Remarks/Total Amount */,
							'& th:nth-of-type(5), & td:nth-of-type(5)': { width: '20%' } /* Action */,
							'& table': { width: '100%' } /* Ensure table takes full container width */,
						}}
					>
						<Table aria-label="Advance Amount table">
							<TableHead>
								<TableRow>
									<TableCell sx={{ textAlign: 'center', backgroundColor: '#f5f5f5' }}>From Month</TableCell>
									<TableCell sx={{ textAlign: 'center', backgroundColor: '#f5f5f5' }}>To Month</TableCell>
									<TableCell sx={{ textAlign: 'center', backgroundColor: '#f5f5f5' }}>Monthly Adjustment</TableCell>
									<TableCell sx={{ textAlign: 'center', backgroundColor: '#f5f5f5' }}> Amount</TableCell>
									<TableCell sx={{ textAlign: 'center', backgroundColor: '#f5f5f5' }}>Action</TableCell>
								</TableRow>
							</TableHead>

							<TableBody>
								{subFields.length > 0 ? (
									subFields.map((subField, aaIndex) => (
										<TableRow key={aaIndex} sx={{ backgroundColor: '#ffffff' }}>
											<TableCell>
												<Form.Item name={[subField.name, 'from']} initialValue={1}>
													<InputNumber
														disabled={true} // Always disabled to prevent manual editing
														style={{
															width: '100%',
															backgroundColor: '#ffffff', // Grey background to indicate disabled
															textAlign: 'center',
														}}
													/>
												</Form.Item>
											</TableCell>

											<TableCell>
												<Form.Item
													name={[subField.name, 'to']}
													rules={[
														{ required: false, message: "Please enter a 'To' value" },
														({ getFieldValue }) => ({
															validator(_, value) {
																// const fromValue = getFieldValue(`LeaseSimulator${index}AdvanceAmount${aaIndex}From`);
																const advanceMonth = getFieldValue([
																	'leaseSimulator',
																	index,
																	'advanceAmountList',
																	aaIndex,
																	'from',
																]);

																const advanceLastRowIndex =
																	(getFieldValue(['leaseSimulator', index, 'advanceAmountList']) || []).length - 1;

																const maxAdvanceMonth = getFieldValue([
																	'leaseSimulator',
																	index,
																	'advanceAmountList',
																	advanceLastRowIndex,
																	'from',
																]);

																const leaseTerm = getFieldValue(['leaseSimulator', index, 'leaseTerm']);

																if (
																	value === undefined ||
																	value === null ||
																	value === false ||
																	isNaN(value) ||
																	value === '' ||
																	advanceMonth === undefined ||
																	advanceMonth === null ||
																	isNaN(advanceMonth) ||
																	advanceMonth === '' ||
																	advanceMonth === false
																) {
																	return Promise.resolve();
																}
																if (value < advanceMonth) {
																	return Promise.reject(new Error("'To' must be greater or equal than 'From'"));
																}
																if (leaseTerm < value || leaseTerm < maxAdvanceMonth) {
																	return Promise.reject(
																		new Error(`'To' must be less than or equal to ${leaseTerm}`)
																	);
																}
																return Promise.resolve();
															},
														}),
													]}
												>
													<InputNumber
														type="number"
														disabled={screen === 'VIEW'}
														placeholder="Enter To value"
														style={{
															width: '100%',
															textAlign: 'center',
														}}
														onChange={() => handleToChange(index, aaIndex)}
														onBlur={() => validateToValueOnBlur(index, aaIndex)}
													/>
												</Form.Item>
											</TableCell>
											<TableCell>
												<Form.Item
													name={[subField.name, 'amount']}
													rules={[{ required: false, message: 'Please enter an amount' }]}
													getValueFromEvent={(values) => {
														const input = values?.target?.value ?? '';
														const cleaned = input.replace(/,/g, ''); // Remove all commas
														const data = parseFloat(cleaned); // parseFloat handles decimals

														return isNaN(data) ? 0 : data;
													}}
												>
													<NumericFormat
														size="small"
														decimalScale={2}
														placeholder="0.00"
														fixedDecimalScale
														thousandSeparator=","
														isAllowed={withValueCap}
														style={{
															textAlign: 'right',
															height: '32px',
															borderRadius: '4px',
															fontSize: '13px',
															backgroundColor: modifiedFields[`leaseSimulator${index}AdvanceAmount${aaIndex}Amount`]
																? '#f0f7ff'
																: undefined,
														}}
														customInput={Input}
														disabled={screen === 'VIEW'}
														onValueChange={() => {
															handleAmountChange(index, aaIndex);
														}}
														onBlur={() => {
															setTimeout(() => {
																calculateRowTotals();
																calculateTotal();
																handleApply();
															}, 10);
														}}
													/>
												</Form.Item>
											</TableCell>
											<TableCell style={{ textAlign: 'right', fontWeight: 'bold' }}>
												<NumericFormat
													value={rowTotals[aaIndex] || 0}
													displayType="input"
													decimalScale={2}
													fixedDecimalScale
													thousandSeparator=","
													size="small"
													style={{
														padding: '18px',
														textAlign: 'right',
														height: '32px',
														borderRadius: '4px',
														fontSize: '13px',
														fontWeight: 'bold',
														backgroundColor: undefined,
														borderColor: undefined,
													}}
													customInput={Input}
													disabled={true}
													readOnly={true}
												/>
											</TableCell>
											<TableCell align="center">
												{screen !== 'VIEW' && screen !== 'EDIT' && (
													<>
														{/* Logic for displaying icons based on row position */}
														{/* First row with only one row in total - only plus icon */}
														{subFields.length === 1 && aaIndex === 0 && (
															<Tooltip title="Add Row">
																<PlusCircleOutlined
																	style={{
																		fontSize: '20px',
																		color: '#1890ff',
																		cursor: 'pointer',
																	}}
																	onClick={() => handleAddRow(index, aaIndex)}
																/>
															</Tooltip>
														)}

														{/* Middle rows - only minus icon */}
														{subFields.length > 1 && aaIndex > 0 && aaIndex < subFields.length - 1 && (
															<Tooltip title="Delete Row">
																<MinusCircleOutlined
																	style={{
																		fontSize: '20px',
																		color: '#ff4d4f',
																		cursor: 'pointer',
																	}}
																	onClick={() => {
																		subFieldsOpt.remove(subField.name);
																		handleDeleteRow(index, aaIndex);
																	}}
																/>
															</Tooltip>
														)}

														{/* Last row (but not first) - both plus and minus icons */}
														{subFields.length > 1 && aaIndex === subFields.length - 1 && (
															<>
																<Tooltip title="Add Row">
																	<PlusCircleOutlined
																		style={{
																			fontSize: '20px',
																			color: '#1890ff',
																			marginRight: '12px',
																			cursor: 'pointer',
																		}}
																		onClick={() => handleAddRow(index, aaIndex)}
																	/>
																</Tooltip>
																<Tooltip title="Delete Row">
																	<MinusCircleOutlined
																		style={{
																			fontSize: '20px',
																			color: '#ff4d4f',
																			cursor: 'pointer',
																		}}
																		onClick={() => {
																			subFieldsOpt.remove(subField.name);
																			handleDeleteRow();
																		}}
																	/>
																</Tooltip>
															</>
														)}

														{/* First row with multiple rows - no icons */}
														{subFields.length > 1 && aaIndex === 0 && <span></span>}
													</>
												)}
											</TableCell>
										</TableRow>
									))
								) : (
									<TableRow>
										<TableCell colSpan={5} style={{ textAlign: 'center' }}>
											No data. Click the plus icon to add new entries.
										</TableCell>
									</TableRow>
								)}
							</TableBody>

							<TableFooter>
								<TableRow sx={{ height: 24 }}>
									<TableCell colSpan={3} align="right" style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
										<InputLabel
											label={'Advance Payment'}
											head={true}
											sx={{
												justifyContent: 'end',
												marginBottom: '0px',
												backgroundColor: '#f5f5f5',
												borderColor: '#f5f5f5',
											}}
										/>
									</TableCell>
									<TableCell style={{ fontWeight: 'bold', textAlign: 'right' }}>
										<NumericFormat
											value={totalAmount || 0}
											displayType="input"
											decimalScale={2}
											fixedDecimalScale
											thousandSeparator=","
											size="small"
											style={{
												padding: '18px',
												textAlign: 'right',
												height: '32px',
												borderRadius: '4px',
												fontSize: '13px',
												fontWeight: 'bold',
												backgroundColor: undefined,
												borderColor: undefined,
											}}
											customInput={Input}
											disabled={true}
											readOnly={true}
										/>
									</TableCell>
									<TableCell colSpan={1}></TableCell>
								</TableRow>
							</TableFooter>
						</Table>
					</TableContainer>
				)}
			</Form.List>
			{/* Hidden form fields to store processed data for API submission */}
			<Form.Item name={['leaseSimulator', index, 'advanceAmounts']} noStyle />
			<Form.Item name={['leaseSimulator', index, 'advanceAmount']} noStyle />
		</>
	);
};

export default AdvanceAmount;